import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Sparkles, Loader2, ArrowRight, Lightbulb, MessageCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getAIRecommendation } from '../lib/aiApi';
import { TREATMENTS, getTreatmentBySlug } from '../data/treatments';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { track } from '../lib/analytics';

const CITIES = ['Ho Chi Minh City', 'Hanoi', 'Da Nang', 'Nha Trang', 'No preference'];
const BUDGETS = ['Under $500', '$500 – $1,500', '$1,500 – $5,000', '$5,000 – $15,000', '$15,000+', 'Not sure yet'];

const FindBestClinicPage = () => {
  const { contactInfo } = useContactInfo();
  const [form, setForm] = useState({ description: '', city: '', budget: '' });
  const [status, setStatus] = useState({ loading: false, error: '' });
  const [result, setResult] = useState(null);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.description.trim().length < 8) {
      setStatus({ loading: false, error: 'Please describe your concern in a bit more detail.' });
      return;
    }
    setStatus({ loading: true, error: '' });
    setResult(null);
    try {
      const res = await getAIRecommendation({
        description: form.description,
        city: form.city,
        budget: form.budget,
      });
      setResult(res);
      track('ai_recommendation_success', {
        treatment: res.treatment_slug,
        confidence: res.confidence,
      });
    } catch (err) {
      setStatus({ loading: false, error: 'Our AI is taking a moment. Please try again or submit a regular request.' });
      track('ai_recommendation_failed', { error: err?.message || 'unknown' });
      return;
    }
    setStatus({ loading: false, error: '' });
  };

  const recommendedTreatment = result ? getTreatmentBySlug(result.treatment_slug) : null;
  const waHref = getWhatsAppHref(
    contactInfo,
    result
      ? `Hi VietClinics! AI recommended ${result.treatment_name} for me — I'd like to discuss next steps.`
      : 'Hi VietClinics!'
  );

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Explore Treatment Information with AI | VietClinics</title>
        <meta name="description" content="Explore general treatment information with VietClinics' AI tool, then request coordinator support. Information is not a diagnosis; providers assess suitability." />
        <meta property="og:title" content="Explore Treatment Information with AI | VietClinics" />
        <meta property="og:description" content="Explore general treatment information and request coordination support. Licensed providers, not AI, assess clinical suitability and recommend care." />
        <link rel="canonical" href="https://vietclinics.com/find-best-treatment" />
      </Helmet>
      <Header />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0f2b55 0%, #1E5BA8 55%, #1FA7A1 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 0%, transparent 50%)' }}
        />
        <div className="container mx-auto px-4 max-w-4xl py-16 lg:py-20 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 text-white/90 text-[11px] font-bold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
            <Sparkles className="h-3.5 w-3.5 text-[#2EE8C4]" />
            AI Concierge — Beta
          </div>
          <h1
            className="font-extrabold tracking-tight leading-[1.05] text-white"
            style={{ fontSize: 'clamp(2.4rem, 4.6vw, 3.6rem)' }}
            data-testid="ai-page-title"
          >
            Find the Best Treatment for Me
          </h1>
          <p className="text-white/85 text-lg mt-5 leading-relaxed max-w-2xl mx-auto">
            Describe your concern in a sentence or two. Our AI concierge will recommend the most suitable treatment from VietClinics' verified catalogue — then connect you with a human concierge to confirm.
          </p>
        </div>
      </section>

      {/* Form + Result */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="bg-white rounded-[28px] border border-slate-100 p-6 sm:p-10"
            style={{ boxShadow: '0 20px 60px rgba(15,42,85,0.08)' }}
          >
            <form onSubmit={submit} className="grid grid-cols-1 gap-4" data-testid="ai-form">
              <label className="block">
                <span className="block text-xs font-semibold text-slate-600 mb-1.5">Describe your concern *</span>
                <textarea
                  required
                  rows={4}
                  value={form.description}
                  onChange={onChange('description')}
                  placeholder="e.g. I lost a molar two years ago and want a permanent replacement that looks natural. I'm 42, generally healthy."
                  className="w-full px-3.5 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#1FA7A1] focus:ring-2 focus:ring-[#1FA7A1]/15 resize-none"
                  data-testid="ai-input-description"
                />
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-xs font-semibold text-slate-600 mb-1.5">Preferred city</span>
                  <select value={form.city} onChange={onChange('city')} className="w-full h-11 px-3.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:border-[#1FA7A1] focus:ring-2 focus:ring-[#1FA7A1]/15" data-testid="ai-input-city">
                    <option value="">Select a city</option>
                    {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold text-slate-600 mb-1.5">Budget range</span>
                  <select value={form.budget} onChange={onChange('budget')} className="w-full h-11 px-3.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:border-[#1FA7A1] focus:ring-2 focus:ring-[#1FA7A1]/15" data-testid="ai-input-budget">
                    <option value="">Select a range</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </label>
              </div>

              {status.error && (
                <p className="text-sm text-red-500 font-medium">{status.error}</p>
              )}

              <button
                type="submit"
                disabled={status.loading}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#178f8a] disabled:opacity-60 text-white font-bold rounded-full px-7 py-3.5 transition-all hover:-translate-y-0.5"
                data-testid="ai-form-submit"
              >
                {status.loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Asking our AI concierge…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" /> Get my recommendation
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Result */}
          {result && recommendedTreatment && (
            <div className="mt-8 bg-white rounded-[28px] border border-slate-100 overflow-hidden" data-testid="ai-result" style={{ boxShadow: '0 20px 60px rgba(15,42,85,0.08)' }}>
              <div className="grid grid-cols-1 md:grid-cols-5">
                <div className="md:col-span-2 h-56 md:h-full overflow-hidden">
                  <img
                    src={recommendedTreatment.image}
                    alt={recommendedTreatment.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:col-span-3 p-6 sm:p-8 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-4 w-4 text-[#1FA7A1]" />
                    <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest">
                      AI Recommendation · {result.confidence} confidence
                    </p>
                  </div>
                  <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                    {recommendedTreatment.name}
                  </h2>
                  <p className="text-sm font-semibold text-slate-500 mt-1">
                    {recommendedTreatment.startingPrice} · Recovery: {recommendedTreatment.recoveryTime}
                  </p>
                  <p className="text-slate-600 mt-4 leading-relaxed text-sm">
                    {result.reasoning}
                  </p>

                  {Array.isArray(result.follow_up_questions) && result.follow_up_questions.length > 0 && (
                    <div className="mt-5 bg-slate-50 rounded-2xl p-4 border border-slate-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="h-4 w-4 text-amber-500" />
                        <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">A few clarifying questions</p>
                      </div>
                      <ul className="space-y-1.5">
                        {result.follow_up_questions.map((q, i) => (
                          <li key={i} className="text-sm text-slate-600 flex gap-2">
                            <span className="text-[#1FA7A1] font-bold">·</span>
                            <span>{q}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3 mt-6">
                    <Link
                      to={`/treatments/${recommendedTreatment.slug}#request-form`}
                      className="inline-flex items-center gap-2 bg-[#1FA7A1] hover:bg-[#178f8a] text-white font-bold rounded-full px-6 py-3 transition-all hover:-translate-y-0.5"
                      data-testid="ai-cta-request"
                    >
                      Request Appointment <ArrowRight className="h-4 w-4" />
                    </Link>
                    {waHref && (
                      <a
                        href={waHref}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 bg-white text-slate-800 border border-slate-200 font-semibold rounded-full px-6 py-3 hover:border-[#1FA7A1] hover:text-[#1FA7A1] transition-all"
                        data-testid="ai-cta-whatsapp"
                      >
                        <MessageCircle className="h-4 w-4" /> Discuss on WhatsApp
                      </a>
                    )}
                  </div>

                  <p className="text-xs text-slate-400 mt-5 leading-relaxed">
                    AI recommendation is informational only and not a medical diagnosis. A VietClinics concierge will confirm the right plan before any appointment is scheduled.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Other treatments grid */}
      <section className="py-16" style={{ background: '#F8FAFC' }}>
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2 text-center">All Available Care</p>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight text-center mb-10">
            Or explore treatments yourself
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TREATMENTS.map((t) => (
              <Link
                key={t.slug}
                to={`/treatments/${t.slug}`}
                className="bg-white rounded-2xl border border-slate-100 p-5 hover:-translate-y-1 transition-all"
                style={{ boxShadow: '0 2px 12px rgba(15,42,85,0.04)' }}
                data-testid={`ai-explore-${t.slug}`}
              >
                <p className="text-[10px] font-bold text-[#1FA7A1] uppercase tracking-wider">{t.category}</p>
                <h3 className="font-extrabold text-slate-900 text-sm mt-1">{t.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{t.startingPrice}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FindBestClinicPage;
